import {IconButton, Tooltip} from "@chakra-ui/react";
import {useContext, useEffect, useMemo, useState} from "react";
import {useLazyQuery, useMutation, useQuery} from "@apollo/client";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {UserContext, UserContextI} from "../../UserContext";
import {registerSoundFileMutation, soundFileLibraryQuery} from "./soundFileQueries";
import SoundFileLibraryDialog, {ExistingSoundOption} from "./SoundFileLibraryDialog";
import {Area, SoundFile, SoundFileLibraryEntry, Spot} from "../types";
import {UploadFileMetadata} from "../upload/Upload";
import {useUploadQueueContext} from "../uploadQueue/UploadQueueContext";
import {BsSoundwave} from "react-icons/bs";
import {versionsQuery} from "../../pages/versions/versionQueries";
import {getSpotsQuery} from "../spots/graphQl";
import {getAreaContentQuery} from "../area/areaQueries";

type Props = {
    onAfterUpload?: () => void | Promise<void>
}

const SoundFileLibraryManager = ({onAfterUpload}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const {enqueueUploads} = useUploadQueueContext();
    const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
    const [loadSoundFileLibrary, {data: soundFileData, loading: soundFileLoading}] = useLazyQuery(soundFileLibraryQuery, {
        fetchPolicy: "network-only"
    });
    const [registerSoundFile] = useMutation(registerSoundFileMutation);

    const areaId = appContext.getArea()?.id;
    const {data: versionData} = useQuery(versionsQuery, {
        variables: {areaId: areaId},
        skip: !areaId || !isDialogOpen
    });
    const latestPublishedAt = useMemo(() => {
        const versions = versionData?.getVersions || [];
        const published = versions.filter((version: any) => typeof version?.publishedAt === "number" && version.publishedAt > 0);
        if (published.length === 0) {
            return undefined;
        }
        return published.reduce((max: number, version: any) => Math.max(max, version.publishedAt), 0);
    }, [versionData]);
    const hasDraftVersion = useMemo(() => {
        const versions = versionData?.getVersions || [];
        return versions.some((version: any) => version?.publishedAt === 0);
    }, [versionData]);
    const needsPublished = isDialogOpen && !!latestPublishedAt;
    const needsDraft = isDialogOpen && hasDraftVersion;
    const {data: publishedSpotData} = useQuery(getSpotsQuery, {
        variables: {areaId: areaId, publishedAt: latestPublishedAt || 0},
        skip: !areaId || !needsPublished || !latestPublishedAt
    });
    const {data: publishedAreaData} = useQuery(getAreaContentQuery, {
        variables: {areaId: areaId, publishedAt: latestPublishedAt || 0},
        skip: !areaId || !needsPublished || !latestPublishedAt
    });
    const {data: draftSpotData} = useQuery(getSpotsQuery, {
        variables: {areaId: areaId, publishedAt: 0},
        skip: !areaId || !needsDraft
    });
    const {data: draftAreaData} = useQuery(getAreaContentQuery, {
        variables: {areaId: areaId, publishedAt: 0},
        skip: !areaId || !needsDraft
    });
    const publishedSpots = useMemo(() => publishedSpotData?.getSpots || [], [publishedSpotData]);
    const draftSpots = useMemo(() => draftSpotData?.getSpots || [], [draftSpotData]);
    const publishedArea = publishedAreaData?.getAreaContent;
    const draftArea = draftAreaData?.getAreaContent;

    const usageMap = useMemo(() => {
        type UsageEntry = {
            label: string
            languageLabel: string
            ambient: boolean
            versions: Set<string>
        };
        const dedupMap = new Map<string, {sound: SoundFile; usages: Map<string, UsageEntry>}>();
        const getUsageKey = (label: string, languageLabel: string, ambient: boolean) => {
            return `${label}||${languageLabel}||${ambient ? "ambient" : "base"}`;
        };
        const addUsage = (fileName: string | undefined, sound: SoundFile, label: string, versionLabel: string) => {
            if (!fileName || !label) {
                return;
            }
            const languageLabel = sound.language || "no language";
            const ambient = !!sound.ambient;
            const usageKey = getUsageKey(label, languageLabel, ambient);
            const existing = dedupMap.get(fileName) || {sound: sound, usages: new Map<string, UsageEntry>()};
            const usageEntry = existing.usages.get(usageKey) || {
                label: label,
                languageLabel: languageLabel,
                ambient: ambient,
                versions: new Set<string>()
            };
            usageEntry.versions.add(versionLabel);
            existing.usages.set(usageKey, usageEntry);
            dedupMap.set(fileName, existing);
        };

        const appendSpotUsage = (spots: Spot[], versionLabel: string) => {
            spots.forEach((spot: Spot) => {
                const spotLabel = spot.name || "Untitled spot";
                (spot.soundFiles || []).forEach((soundFile) => {
                    addUsage(soundFile.fileName, soundFile, spotLabel, versionLabel);
                });
            });
        };

        const appendZoneUsage = (areaSource: Area | any, versionLabel: string) => {
            const zones = areaSource?.zones || {};
            const baseZones = Object.entries(zones)
                .filter(([zoneId, zone]) => zoneId !== "ambientZones" && zone)
                .map(([, zone]) => zone);
            const ambientZones = Array.isArray(zones.ambientZones) ? zones.ambientZones : [];
            const allZones = [...baseZones, ...ambientZones].filter((zone: any) => zone);
            allZones.forEach((zone: any) => {
                const rawLabel = zone?.label || zone?.name || zone?.id;
                if (!rawLabel) {
                    return;
                }
                const normalizedLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
                const zoneLabel = `Zone: ${normalizedLabel}`;
            const zoneSoundFiles = zone?.soundFiles ? Object.values(zone.soundFiles) as SoundFile[] : [];
                zoneSoundFiles.forEach((zoneSound) => {
                    if (zoneSound) {
                        addUsage(zoneSound.fileName, zoneSound, zoneLabel, versionLabel);
                    }
                });
            });
        };

        if (needsPublished) {
            appendSpotUsage(publishedSpots, "Published");
            appendZoneUsage(publishedArea, "Published");
        }
        if (needsDraft) {
            appendSpotUsage(draftSpots, "Draft");
            appendZoneUsage(draftArea, "Draft");
        }
        const flattened = new Map<string, ExistingSoundOption>();
        const versionOrder = ["Published", "Draft"];
        dedupMap.forEach((value, fileName) => {
            const spotNames = Array.from(value.usages.values()).map((usage) => {
                const versions = versionOrder.filter((ver) => usage.versions.has(ver));
                const versionLabel = versions.length > 0 ? versions.join(" + ") : "Unknown";
                const parts = [usage.languageLabel];
                if (usage.ambient) {
                    parts.push("ambient");
                }
                parts.push(versionLabel);
                return `${usage.label} (${parts.join(", ")})`;
            });
            flattened.set(fileName, {
                sound: value.sound,
                spotNames: spotNames
            });
        });
        return flattened;
    }, [draftArea, draftSpots, needsDraft, needsPublished, publishedArea, publishedSpots]);

    const existingSoundOptions = useMemo(() => {
        const libraryEntries: SoundFileLibraryEntry[] = soundFileData?.getSoundFileLibrary || [];
        return libraryEntries.reduce<ExistingSoundOption[]>((acc, entry) => {
            const fileName = entry.objectId;
            if (!fileName) {
                return acc;
            }
            const usage = usageMap.get(fileName);
            const sound: SoundFile = usage?.sound || {
                originalFileName: entry.originalFileName,
                fileName: fileName,
                filePath: entry.filePath,
                playOnes: true,
                fadeIn: 0,
                fadeOut: 0,
                delay: 0,
                synchronize: false,
                noSyncTime: 0,
                volumeDb: 0
            };
            acc.push({
                sound: sound,
                spotNames: usage?.spotNames || [],
                libraryEntry: entry
            });
            return acc;
        }, []);
    }, [soundFileData, usageMap]);

    useEffect(() => {
        if (!isDialogOpen || !areaId) {
            return;
        }
        loadSoundFileLibrary({
            variables: {
                areaId: areaId
            }
        });
    }, [areaId, isDialogOpen, loadSoundFileLibrary]);

    const handleUpload = async (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        if (!areaId) {
            return;
        }
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        try {
            await registerSoundFile({
                variables: {
                    areaId: areaId,
                    objectId: fileName,
                    originalFileName: originalFileName,
                    filePath: filePath,
                    uploadedBy: uploadedBy,
                    mimeType: metadata?.mimeType,
                    sizeBytes: metadata?.sizeBytes,
                    durationMs: metadata?.durationMs
                }
            });
        } catch (error) {
            console.warn("Unable to register sound file", error);
        }
        loadSoundFileLibrary({
            variables: {
                areaId: areaId
            }
        });
        if (onAfterUpload) {
            await onAfterUpload();
        }
    };

    return (
        <>
            <Tooltip label={"Manage sound files"} hasArrow>
                <IconButton
                    aria-label={"Manage sound files"}
                    icon={<BsSoundwave/>}
                    size={"lg"}
                    variant={"ghost"}
                    isDisabled={!areaId}
                    onClick={() => setDialogOpen(true)}
                />
            </Tooltip>
            <SoundFileLibraryDialog
                isOpen={isDialogOpen}
                onClose={() => setDialogOpen(false)}
                mode={"library"}
                existingSoundOptions={existingSoundOptions}
                isLoading={soundFileLoading}
                onUpload={handleUpload}
                allowMultipleUpload={true}
                enqueueUploads={enqueueUploads}
                onAfterUploadBatch={() => {
                    if (areaId) {
                        loadSoundFileLibrary({
                            variables: {
                                areaId: areaId
                            }
                        });
                    }
                }}
                onAfterDelete={() => {
                    if (areaId) {
                        loadSoundFileLibrary({
                            variables: {
                                areaId: areaId
                            }
                        });
                    }
                }}
            />
        </>
    );
};

export default SoundFileLibraryManager;
